// ============================================================
// ROSTER FACTORIES - pure constructors for the unified combatant
// record. No React, no I/O, no resolution: these read straight off
// a Character, so this module never depends on ./projection.
// ============================================================
import type { Cover, ForceField, Target } from '../../types';
import type { Character, CharacterWeapon, WeaponAmmo } from '../character/types';
import type { Combatant, CombatState, CombatantKind, DefenderSheet, Loadout } from './types';
import { emptyCharacter } from '../character/factories';
import { normalizeCharacter } from '../character/normalize';
import deepClone from '../../utils/deepClone';

const DEFAULT_FORCE_FIELD: ForceField = { active: false, protectionRating: 0, overloadThreshold: 0 };
const DEFAULT_COVER: Cover = { level: 'none', ap: 0 };

/**
 * Monotonic id counter - deliberately NOT Math.random(). Exported for the
 * scenes engine, whose records and re-minted casts must come from the same
 * clock as every combatant (v8.333.0).
 *
 * The E2E harness stubs Math.random() with a deterministic dice sequence
 * (`setDiceSequence`), so any engine code that consumes a random value
 * steals a die from the test and silently shifts every subsequent roll.
 * That is a genuinely hard failure to diagnose: combat math tests start
 * failing in ways that look like damage-calculation regressions.
 */
let idCounter = 0;
export function mintId(prefix: string): string {
  idCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${idCounter.toString(36)}`;
}

/** Characteristic value that yields the given bonus (bonus = value / 10, rounded down). */
function valueFromBonus(bonus: number): number {
  return Math.max(0, Math.round(bonus * 10));
}

/**
 * Build the per-weapon ammo map for a fresh combatant.
 *
 * A Character is a template: its ammo entry is magazine CAPACITY (the editor
 * exposes Max only), so every weapon enters combat FULL - the same rule
 * resolveCombatWeapon applies (v6.38.3). Under the roster that rule belongs
 * here, because ammo is now state rather than a projection concern.
 */
export function initialWeaponAmmo(character: Character): Record<string, WeaponAmmo> {
  const ammo: Record<string, WeaponAmmo> = {};
  for (const weapon of character.weapons ?? []) {
    ammo[weapon.id] = { ...weapon.ammo, current: weapon.ammo.max };
  }
  return ammo;
}

/**
 * The mutable half at full health - the roster's replacement for
 * emptyCombatInstance(). Reads raw character fields; no resolution.
 */
export function emptyCombatState(character: Character): CombatState {
  return {
    rev: 0,
    woundsCurrent: character.wounds?.current ?? character.wounds?.max ?? 15,
    hordeMagnitude: character.horde?.magnitude ?? 0,
    conditions: [],
    criticalDamage: [],
    armour: { ...(character.armour?.locations ?? {}) },
    forceField: { ...(character.forceField ?? DEFAULT_FORCE_FIELD) },
    weaponAmmo: initialWeaponAmmo(character),
    cover: { ...(character.cover ?? DEFAULT_COVER) },
    roundCountdown: false,
    countdownTickedThisRound: false,
    defenderCustomMods: [],
    defensiveStance: false,
    frenzied: false,
    // AUTO, not front: the map answers until somebody rules otherwise.
    vehicleFacing: character.bodyType === 'vehicle' ? 'auto' : null,
  };
}

interface CombatantOptions {
  id?: string;
  name?: string;
  kind?: CombatantKind;
  overrides?: Partial<DefenderSheet>;
  order?: number;
  state?: CombatState;
}

/**
 * The entry's sheet alone: a saved condition is a LIBRARY fact and never
 * rides on a live sheet (see engine/roster/standing.ts, which owns it).
 */
export function withoutStanding(character: Character): Character {
  if (!character.standing) return character;
  const { standing: _standing, ...sheet } = character;
  return sheet as Character;
}

/**
 * Wrap a Character into a live roster record at full health.
 * The character is deep-cloned so later library edits cannot mutate it in
 * place - the projection caches key on Character identity and rely on it.
 *
 * READ ONCE HERE (2026-10-28): every way a combatant is made passes this door (the server's records pass
 * `combatantFromDoc`, which normalizes too), so a roster sheet is always normalized - its Always on items and Solo
 * modes read and typed - and play trusts them as typed from here on. Idempotent: a normalized sheet comes out the same.
 */
export function combatantFromCharacter(character: Character, opts: CombatantOptions = {}): Combatant {
  const now = Date.now();
  const sheet = deepClone(withoutStanding(normalizeCharacter(character)));
  return {
    id: opts.id || mintId('cbt'),
    // `||` so an empty name falls through to the sheet's - see combatantFromDoc.
    name: opts.name || sheet.name,
    kind: opts.kind ?? 'character',
    sheet,
    overrides: opts.overrides ?? {},
    state: opts.state ?? emptyCombatState(sheet),
    loadoutRef: null,
    narrativeLog: [],
    order: opts.order ?? 0,
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Set a fresh sheet's characteristics so the resolver gives back the
 * statblock's figures. Writes into `chars`, which belongs to a character
 * built a moment ago and seen by nobody yet.
 *
 * SB and WPB are stored EFFECTIVE on a statblock (v8.197.3); the shell's
 * characteristic is the base, and the multiplier rides beside it, so the
 * resolver multiplies back to the same effective bonus (v8.310.11).
 */
function setCharacteristicsFrom(chars: Character['characteristics'], target: Target): void {
  chars.ws.value = target.parry?.weaponSkill ?? 30;
  chars.bs.value = target.parry?.weaponSkill ?? 30;
  chars.agi.value = target.dodge?.agility ?? 30;
  chars.tgh.value = valueFromBonus(target.toughnessBonus ?? 3);
  chars.tgh.unnaturalMultiplier = target.unnaturalToughness || 1;
  chars.agi.unnaturalMultiplier = target.unnaturalAgility || 1;
  chars.ws.unnaturalMultiplier = target.unnaturalWeaponSkill || 1;
  const unnatStr = target.unnaturalStrength || 1;
  const unnatWp = target.unnaturalWillpower || 1;
  chars.str.value = valueFromBonus(Math.round((target.strengthBonus ?? 3) / unnatStr));
  chars.str.unnaturalMultiplier = unnatStr;
  chars.wp.value = valueFromBonus(Math.round((target.willpowerBonus ?? 3) / unnatWp));
  chars.wp.unnaturalMultiplier = unnatWp;
}

/**
 * Synthesise a Character shell from a plain statblock.
 *
 * The shell exists so the shared Overview sheet, skills card, characteristic
 * grid and dice tray work on nameless mooks too. Everything that does NOT
 * invert cleanly into talents/traits (naturalArmour, machineAP, trueGrit, …)
 * stays out of here and rides in Combatant.overrides instead.
 */
export function characterFromStatblock(target: Target, id?: string): Character {
  const base = emptyCharacter(id);
  const chars = base.characteristics;
  setCharacteristicsFrom(chars, target);

  return {
    ...base,
    name: target.name || 'Statblock',
    characteristics: chars,
    skills: {
      ...base.skills,
      dodge: { ...base.skills.dodge, trained: !!target.dodge?.canDodge },
      parry: { ...base.skills.parry, trained: !!target.parry?.canParry },
    },
    armour: { name: 'Statblock', locations: { ...(target.armour ?? {}) } },
    forceField: { ...(target.forceField ?? DEFAULT_FORCE_FIELD) },
    cover: { ...(target.cover ?? DEFAULT_COVER) },
    horde: { ...(target.horde ?? { isHorde: false, magnitude: 0, maxMagnitude: 0 }) },
    size: target.size || 'Average',
    fearRating: target.fearRating ?? 0,
    psyRating: target.psyRating ?? 0,
    xenos: !!target.xenos,
    wounds: { ...(target.wounds ?? { current: 15, max: 15 }) },
    bodyType: target.bodyType,
  };
}

/** A statblock combatant: synthesised shell + the flat values carried as overrides. */
export function combatantFromStatblock(
  target: Target,
  overrides: Partial<DefenderSheet>,
  opts: CombatantOptions = {},
): Combatant {
  const sheet = characterFromStatblock(target);
  return combatantFromCharacter(sheet, {
    ...opts,
    kind: 'statblock',
    name: opts.name || target.name,
    overrides,
  });
}

export function emptyLoadout(name = 'New Loadout', id?: string): Loadout {
  const now = Date.now();
  return {
    id: id || mintId('ldt'),
    name,
    weapons: [],
    tags: [],
    createdAt: now,
    updatedAt: now,
  };
}

export function loadoutFromWeapons(name: string, weapons: CharacterWeapon[], id?: string): Loadout {
  return { ...emptyLoadout(name, id), weapons: deepClone(weapons) };
}
